import { AnimatePresence, motion } from 'framer-motion';
import {
  Check,
  Download,
  ExternalLink,
  FileCheck,
  Loader2,
  Terminal,
  X,
  Zap,
} from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';
import { useGetBlobDetailQuery } from '@/services/api/blobService';
import { useGetFrontConversationByDbIdQuery } from '@/services/api/frontMessageService';
import {
  RainmakerEvidence,
  RainmakerEvidenceType,
  RainmakerSuggestion,
} from '@/services/types/rainmaker-types';

import { EvidencePreview } from './EvidencePreview';
import { RainmakerLogo } from './RainmakerLogo';
import { getEvidenceIcon } from './utils';

interface EvidenceModalProps {
  suggestion: RainmakerSuggestion;
  matterId: string;
  onClose: () => void;
  onExecute: () => Promise<void>;
  isExecuting: boolean;
}

// Helper component for download button
const EvidenceDownloadButton: React.FC<{
  evidence: RainmakerEvidence;
  matterId: string;
}> = ({ evidence, matterId }) => {
  const shouldFetchBlob =
    evidence.type === RainmakerEvidenceType.BLOB ||
    evidence.type === RainmakerEvidenceType.ARTIFACT ||
    evidence.type === RainmakerEvidenceType.DOCUMENT;

  const { data: blobData } = useGetBlobDetailQuery(
    { matterId, blobId: evidence.id },
    { skip: !shouldFetchBlob },
  );

  if (!shouldFetchBlob || !blobData) {
    return null;
  }

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(blobData.download_url, '_blank');
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      className="rounded p-1 text-slate-400 transition-colors hover:bg-violet-100 hover:text-violet-600"
      title="Download"
    >
      <Download size={12} />
    </button>
  );
};

// Helper component for Front conversation link
const FrontConversationButton: React.FC<{
  evidence: RainmakerEvidence;
  matterId: string;
}> = ({ evidence, matterId }) => {
  // Fetch the Front conversation by database ID to get the actual Front API ID
  // Only skip the query if it's not a Front conversation type
  const { data: frontConversationData } = useGetFrontConversationByDbIdQuery(
    {
      matterId,
      conversationDbId: evidence.id,
    },
    {
      skip: evidence.type !== RainmakerEvidenceType.FRONT_CONVERSATION,
    },
  );

  // Only show for Front conversation types
  if (evidence.type !== RainmakerEvidenceType.FRONT_CONVERSATION) {
    return null;
  }

  const handleOpenFront = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Get the Front API conversation ID from the fetched data
    const frontConversationId = frontConversationData?.conversation?.id;

    if (!frontConversationId) {
      return;
    }

    // Construct Front URL using the pattern: https://app.frontapp.com/open/{conversation_id}
    const frontUrl = `https://app.frontapp.com/open/${frontConversationId}`;
    window.open(frontUrl, '_blank');
  };

  // Only show the button if we have the conversation data
  if (!frontConversationData?.conversation?.id) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleOpenFront}
      className="rounded p-1 text-slate-400 transition-colors hover:bg-violet-100 hover:text-violet-600"
      title="Open in Front"
    >
      <ExternalLink size={12} />
    </button>
  );
};

export const EvidenceModal: React.FC<EvidenceModalProps> = ({
  suggestion,
  matterId,
  onClose,
  onExecute,
  isExecuting,
}) => {
  const [executedActions, setExecutedActions] = useState<Set<number>>(
    new Set(),
  );
  const [currentExecutingIndex, setCurrentExecutingIndex] = useState<
    number | null
  >(null);
  const [selectedEvidenceIndex, setSelectedEvidenceIndex] = useState<
    number | null
  >(null);

  const handleExecute = async () => {
    // Simulate sequential execution with animations
    for (let i = 0; i < suggestion.actions.length; i += 1) {
      setCurrentExecutingIndex(i);
      // Small delay to show the animation
      // eslint-disable-next-line no-await-in-loop
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 400);
      });
      setExecutedActions((prev) => new Set(prev).add(i));
    }
    setCurrentExecutingIndex(null);

    // Actually execute
    await onExecute();
  };

  // Prevent background scroll when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-sm sm:p-6"
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="relative flex h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      >
        {/* Modal Header: Rainmaker Branding */}
        <div className="relative h-14 shrink-0 overflow-hidden bg-[#0B0F19] px-4">
          {/* Animated background */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-900/40 via-[#0B0F19] to-[#000000]" />
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-violet-500/10 to-transparent"
            animate={{ x: ['-100%', '100%'] }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
          <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-violet-500/30" />

          <div className="relative z-10 flex h-full items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-violet-500/40 bg-violet-500/20">
                <RainmakerLogo size={14} className="text-violet-300" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-violet-200">
                  Rainmaker Insight
                </div>
                <h2 className="text-xs font-semibold tracking-tight text-white">
                  {suggestion.title}
                </h2>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-white/5 p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Modal Body: Split View */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left: Evidence */}
          <div className="flex w-1/2 flex-col border-r border-slate-200 bg-slate-50">
            <div className="border-b border-slate-200 bg-white/50 px-3 py-2">
              <h3 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <FileCheck size={12} /> Detected Evidence
              </h3>
            </div>
            <div className="flex flex-1 flex-col overflow-hidden">
              {/* Evidence List */}
              <div
                className={cn(
                  'overflow-y-auto',
                  selectedEvidenceIndex !== null ? 'flex-[0_0_25%]' : 'flex-1',
                )}
              >
                {suggestion.evidence && suggestion.evidence.length > 0 ? (
                  <div className="divide-y divide-slate-200">
                    {suggestion.evidence.map((evidence, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          'flex items-center gap-2 px-3 py-2 transition-colors',
                          selectedEvidenceIndex === idx &&
                            'bg-violet-50 border-l-2 border-l-violet-500',
                        )}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedEvidenceIndex(
                              idx === selectedEvidenceIndex ? null : idx,
                            )
                          }
                          className="flex min-w-0 flex-1 items-center gap-2 text-left hover:opacity-70"
                        >
                          <div className="flex h-6 w-6 shrink-0 items-center justify-center text-violet-600">
                            {getEvidenceIcon(evidence.type)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="truncate text-[10px] font-semibold leading-tight text-slate-900">
                              {evidence.display_name}
                            </h4>
                            <p className="text-[9px] text-slate-500">
                              {evidence.type}
                            </p>
                          </div>
                        </button>
                        <div className="flex items-center gap-1">
                          <FrontConversationButton
                            evidence={evidence}
                            matterId={matterId}
                          />
                          <EvidenceDownloadButton
                            evidence={evidence}
                            matterId={matterId}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-slate-400">
                    No evidence available
                  </div>
                )}
              </div>

              {/* Evidence Preview */}
              {selectedEvidenceIndex !== null &&
                suggestion.evidence &&
                suggestion.evidence[selectedEvidenceIndex] && (
                  <div className="flex-[0_0_75%] min-h-0 border-t border-slate-200 bg-white">
                    <div className="flex h-full flex-col">
                      <div className="shrink-0 border-b border-slate-200 px-3 py-2">
                        <h4 className="text-[10px] font-bold text-slate-900">
                          Preview
                        </h4>
                      </div>
                      <div className="flex-1 min-h-0">
                        <EvidencePreview
                          evidence={suggestion.evidence[selectedEvidenceIndex]}
                          matterId={matterId}
                        />
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>

          {/* Right: Action Plan */}
          <div className="flex w-1/2 flex-col bg-white">
            <div className="border-b border-slate-200 bg-white/50 px-3 py-2">
              <h3 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <Terminal size={12} /> Action Plan
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="mb-3">
                <p className="text-[10px] font-semibold uppercase text-slate-500">
                  The system will automatically execute:
                </p>
              </div>

              {/* Action Steps with Vertical Timeline */}
              <div className="relative space-y-4 border-l-2 border-slate-200 pl-4">
                <AnimatePresence>
                  {suggestion.actions && suggestion.actions.length > 0 ? (
                    suggestion.actions.map((action, idx) => {
                      const isExecuted = executedActions.has(idx);
                      const isExecutingAction = currentExecutingIndex === idx;

                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="relative"
                        >
                          {/* Timeline Node */}
                          <div
                            className={cn(
                              'absolute -left-[23px] top-0 h-3 w-3 rounded-full border-2 transition-colors',
                              isExecuted && 'border-green-500 bg-green-500',
                              isExecutingAction &&
                                'border-violet-500 bg-violet-500',
                              !isExecuted &&
                                !isExecutingAction &&
                                'border-violet-500 bg-white',
                            )}
                          >
                            {isExecutingAction && (
                              <motion.div
                                className="absolute inset-0 -m-1 rounded-full border-2 border-violet-300"
                                animate={{
                                  scale: [1, 1.5, 1],
                                  opacity: [1, 0, 1],
                                }}
                                transition={{
                                  duration: 1.5,
                                  repeat: Infinity,
                                  ease: 'easeInOut',
                                }}
                              />
                            )}
                          </div>

                          {/* Step Content */}
                          <div className="flex flex-col">
                            <div className="mb-0.5 flex items-center gap-2">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                STEP {String(idx + 1).padStart(2, '0')}
                              </span>
                              {isExecutingAction && (
                                <span className="flex items-center gap-1 text-[10px] font-medium text-violet-600">
                                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                  Executing
                                </span>
                              )}
                              {isExecuted && !isExecutingAction && (
                                <motion.span
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  className="flex items-center gap-1 text-[10px] font-medium text-green-600"
                                >
                                  <Check className="h-2.5 w-2.5" />
                                  Complete
                                </motion.span>
                              )}
                            </div>
                            <h4 className="text-xs font-semibold text-slate-900">
                              {action.description}
                            </h4>
                            <p className="mt-0.5 text-[10px] text-slate-500">
                              Type: {action.type}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })
                  ) : (
                    <div className="relative">
                      <div className="absolute -left-[25px] top-1 h-3 w-3 rounded-full border-2 border-violet-500 bg-white" />
                      <div className="flex flex-col">
                        <span className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          STEP 01
                        </span>
                        <h4 className="text-xs font-semibold text-slate-900">
                          {suggestion.cta_label}
                        </h4>
                        <p className="mt-0.5 text-[10px] text-slate-500">
                          System will execute automatically
                        </p>
                      </div>
                    </div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Footer CTA */}
            <div className="border-t border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[10px] text-slate-600">
                  <strong className="font-semibold text-slate-900">
                    {suggestion.actions?.length || 1} actions
                  </strong>{' '}
                  will be executed by the system.
                </div>
                <button
                  type="button"
                  onClick={handleExecute}
                  disabled={isExecuting}
                  className={cn(
                    'group flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-1.5 text-[10px] font-bold text-white shadow-lg shadow-violet-900/10 transition-all hover:scale-[1.02] hover:bg-violet-600 text-nowrap',
                    isExecuting && 'cursor-not-allowed opacity-50',
                  )}
                >
                  {isExecuting ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Executing...</span>
                    </>
                  ) : (
                    <>
                      <Zap
                        size={14}
                        className="transition-colors group-hover:text-yellow-300"
                        fill="currentColor"
                      />
                      {suggestion.cta_label}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
